import React, { useEffect, useState, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Store, Shield, ArrowRight, Layers, Loader2, UtensilsCrossed, ImageIcon, CheckCircle2, XCircle, MessageCircle, Eye, EyeOff, Clock, AlertCircle, Inbox, Check, X, FileText, BarChart3, Receipt, LogOut, UserIcon, ChefHat, ShieldCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { useIdleLogout } from '../../lib/useIdleLogout';
interface RestaurantApplication {
  id: string;
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string | null;
  business_type: string;
  uses_whatsapp_orders: boolean;
  has_digital_menu: boolean;
  main_goal: string | null;
  status: 'pending' | 'contacted' | 'approved' | 'rejected';
  created_at: string;
}
const ENROLLMENT_OPTIONS = [{
  title: 'Conductor',
  description: 'Registrar conductor, crear o reutilizar vehículo y dejar accesos listos',
  route: '/admin/drivers/new',
  icon: Car,
  accent: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  iconBg: 'bg-blue-500/15',
  btnClass: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
}, {
  title: 'Subir restaurante',
  description: 'Registrar restaurante, crear acceso inicial y dejarlo listo para operar',
  route: '/admin/enrollment/restaurant',
  icon: Store,
  accent: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  iconBg: 'bg-purple-500/15',
  btnClass: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
}, {
  title: 'Generar Recibo',
  description: 'Crear recibos de pago profesionales para clientes, imprimir o compartir por WhatsApp',
  route: '/admin/enrollment/receipts',
  icon: Receipt,
  accent: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  iconBg: 'bg-emerald-500/15',
  btnClass: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
}, {
  title: 'Admin de NOIZA',
  description: 'Crear usuario interno de NOIZA y asignar permisos de plataforma',
  route: '/admin/enrollment/platform-admin',
  icon: Shield,
  accent: 'bg-[#006847]/10 border-[#006847]/20 text-[#00a870]',
  iconBg: 'bg-[#006847]/20',
  btnClass: 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30'
}, {
  title: 'Admin de Restaurante',
  description: 'Asignar usuario existente como administrador de KDS para un restaurante.',
  route: '/admin/enrollment/restaurant-admin',
  icon: ShieldCheck,
  accent: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
  iconBg: 'bg-indigo-500/15',
  btnClass: 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30',
  btnLabel: 'Asignar admin'
}, {
  title: 'Actualizar menú',
  description: 'Agregar categorías, productos u opciones a un restaurante existente sin recrearlo',
  route: '/admin/enrollment/menu-update',
  icon: UtensilsCrossed,
  accent: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  iconBg: 'bg-amber-500/15',
  btnClass: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
  btnLabel: 'Iniciar actualización'
}, {
  title: 'Subir imágenes del menú',
  description: 'Relaciona imágenes con productos del menú usando el nombre del archivo',
  route: '/admin/enrollment/menu-images',
  icon: ImageIcon,
  accent: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
  iconBg: 'bg-teal-500/15',
  btnClass: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
  btnLabel: 'Abrir módulo'
}, {
  title: 'Métricas NOIZA',
  description: 'Revisa métricas globales, crecimiento y operación de NOIZA',
  route: '/admin/enrollment/metrics',
  icon: BarChart3,
  accent: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  iconBg: 'bg-rose-500/15',
  btnClass: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30',
  btnLabel: 'Ver métricas'
}];
export function EnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // --- Access Guard State ---
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // --- Idle Logout ---
  const {
    showWarning: showIdleWarning
  } = useIdleLogout({
    enabled: guardState === 'authorized',
    timeoutMs: 900000,
    warningBeforeMs: 60000,
    redirectTo: '/admin/login'
  });
  // --- Active Restaurants State ---
  const [restaurants, setRestaurants] = useState<{
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    show_in_marketplace: boolean;
  }[]>([]);
  const [restLoading, setRestLoading] = useState(true);
  const [togglingVisibilityId, setTogglingVisibilityId] = useState<string | null>(null);
  // --- Applications State ---
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'approved' | 'rejected'>('all');
  const [selectedApp, setSelectedApp] = useState<RestaurantApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', {
      replace: true
    });
  };
  const fetchApplications = async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_applications').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setAppsError(err.message);
    } finally {
      setAppsLoading(false);
    }
  };
  // ─── Access Guard ─────────────────────────────────────────────
  useEffect(() => {
    async function guard() {
      try {
        // 1. Check authenticated user
        const {
          data: {
            user
          },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('[EnrollmentPage] No authenticated user — redirecting to /admin/login');
          setGuardState('redirecting');
          navigate('/admin/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
          return;
        }
        // 2. Check platform_admins table first (primary source)
        let accessRole: string | null = null;
        const {
          data: platformData,
          error: platformError
        } = await supabase.from('platform_admins').select('role').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        if (!platformError && platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin')) {
          accessRole = platformData.role;
        }
        // 3. Fallback: check restaurant_users table
        if (!accessRole) {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
          if (roleError) {
            console.error('[EnrollmentPage] Role check error:', roleError);
          }
          if (roleData) {
            accessRole = roleData.role;
          }
        }
        if (!accessRole) {
          console.log('[EnrollmentPage] User lacks required role — redirecting to /');
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        console.log('[EnrollmentPage] Access granted:', {
          email: user.email,
          role: accessRole
        });
        setSessionEmail(user.email || null);
        setSessionUserId(user.id);
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentPage] Guard error:', err);
        setGuardState('redirecting');
        navigate('/admin/login', {
          replace: true
        });
      }
    }
    guard();
  }, [navigate, location.pathname]);
  const fetchRestaurants = async () => {
    setRestLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').select('id, name, slug, logo_url, show_in_marketplace').eq('is_active', true).order('name', {
        ascending: true
      });
      if (error) throw error;
      setRestaurants(data || []);
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestLoading(false);
    }
  };
  const toggleMarketplaceVisibility = async (restaurantId: string, currentValue: boolean) => {
    setTogglingVisibilityId(restaurantId);
    const newValue = !currentValue;
    // Optimistic update
    setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
      ...r,
      show_in_marketplace: newValue
    } : r));
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').update({
        show_in_marketplace: newValue
      }).eq('id', restaurantId).select('id, show_in_marketplace');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('La actualización fue bloqueada por RLS. Corré FIX_RLS_RESTAURANTS_MARKETPLACE_TOGGLE.sql en Supabase.');
      }
      setToast({
        message: newValue ? 'Restaurante visible en el marketplace' : 'Restaurante oculto del marketplace',
        type: 'success'
      });
    } catch (err: any) {
      console.error('Error toggling marketplace visibility:', err);
      // Revert on error
      setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
        ...r,
        show_in_marketplace: currentValue
      } : r));
      setToast({
        message: 'No se pudo actualizar la visibilidad',
        type: 'error'
      });
    } finally {
      setTogglingVisibilityId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  useEffect(() => {
    if (guardState === 'authorized') {
      fetchApplications();
      fetchRestaurants();
    }
  }, [guardState]);
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      const {
        error
      } = await supabase.from('restaurant_applications').update({
        status: newStatus
      }).eq('id', id);
      if (error) throw error;
      setToast({
        message: 'Estado actualizado correctamente',
        type: 'success'
      });
      await fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({
          ...selectedApp,
          status: newStatus
        });
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setToast({
        message: 'Error al actualizar estado',
        type: 'error'
      });
    } finally {
      setUpdatingId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  const handleWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    if (cleanNumber) {
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };
  const filteredApps = applications.filter(app => statusFilter === 'all' || app.status === statusFilter);
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20" data-id="element-4002" data-mp="26pje4u0g0j4k">
            Pendiente
          </span>;
      case 'contacted':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-id="element-4003" data-mp="2fppqgckend25">
            Contactado
          </span>;
      case 'approved':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" data-id="element-4004" data-mp="2pezg7lrs0jn3">
            Aprobado
          </span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-4005" data-mp="2zbud608eibz9">
            Rechazado
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/20" data-id="element-4006" data-mp="1520jjdhcs3jh">
            {status}
          </span>;
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4007" data-mp="9q0wbxko5vb6">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-4008" data-mp="npifnxf74gd5" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-4009" data-mp="iqfmba8kvyl0">
      {/* Idle Warning Banner */}
      <AnimatePresence data-id="element-4010" data-mp="3r68tgyh0rxpe">
        {showIdleWarning && <motion.div initial={{
        y: -60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -60,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center px-4 py-3 bg-amber-500/90 text-[#0B0B0B]" data-id="element-4011" data-mp="3r6s8o7fac7zq">
            <Clock className="w-4 h-4 mr-2 shrink-0" data-id="element-4012" data-mp="1iyvsny0w4et7" />
            <span className="text-sm font-bold" data-id="element-4013" data-mp="1ixazabqbb1wx">
              Por seguridad, tu sesión se cerrará pronto por inactividad.
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-id="element-4014" data-mp="19zifovdk583q">
        {/* Header */}
        <div className="flex items-center justify-between gap-4" data-id="element-4015" data-mp="1e02d8021mdqe">
          <div className="flex items-center gap-4" data-id="element-4016" data-mp="21ovdu9tm19rc">
            <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-4017" data-mp="21n0qkouv95k3">
              <Layers className="w-7 h-7 text-[#006847]" data-id="element-4018" data-mp="1sop5f2crqzz1" />
            </div>
            <div data-id="element-4019" data-mp="1sqjf1kilb5nt">
              <h1 className="text-3xl font-black tracking-tight" data-id="element-4020" data-mp="1xni6twes70iu">
                NOIZA Enrollment
              </h1>
              <p className="text-white/40 text-sm mt-1" data-id="element-4021" data-mp="h8qpqfn35fou">
                Gestiona los diferentes tipos de registro y acceso dentro de
                NOIZA
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-4022" data-mp="2nxsfyfw8eysi">
            {/* Session Info Button */}
            <div className="relative" data-id="element-4023" data-mp="2iy61k34o3ci4">
              <button onClick={() => setShowSessionInfo(!showSessionInfo)} className="flex items-center gap-2 px-3 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4024" data-mp="2iz7mczbhjdd1">
                <UserIcon className="w-4 h-4" data-id="element-4025" data-mp="254ybwh05cr32" />
                <span className="hidden sm:inline max-w-[160px] truncate" data-id="element-4026" data-mp="207okivt765cl">
                  {sessionEmail || 'Usuario'}
                </span>
              </button>

              {/* Session Info Dropdown */}
              <AnimatePresence data-id="element-4027" data-mp="1r8l3ryhi6219">
                {showSessionInfo && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 top-full mt-2 w-72 bg-[#141414] border border-white/10 rounded-2xl p-4 shadow-2xl z-50" data-id="element-4028" data-mp="3pne9x72d6juz">
                    <div className="space-y-3" data-id="element-4029" data-mp="tejt9oyibz8c">
                      <div data-id="element-4030" data-mp="xfc3akf7e85o">
                        <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-4031" data-mp="xf2l3j0eh5aj">
                          Sesión activa
                        </p>
                        <p className="text-sm font-bold text-white break-all" data-id="element-4032" data-mp="28uraeh14mgei">
                          {sessionEmail || '—'}
                        </p>
                      </div>
                      {sessionUserId && <div data-id="element-4033" data-mp="1pg8lnvb5n8ds">
                          <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-4034" data-mp="1pha0mavakf0s">
                            User ID
                          </p>
                          <p className="text-[11px] font-mono text-white/50 break-all" data-id="element-4035" data-mp="1yadkp20fzq4e">
                            {sessionUserId}
                          </p>
                        </div>}
                      <div className="pt-2 border-t border-white/5" data-id="element-4036" data-mp="285yi0mukg27t">
                        <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl text-sm font-bold transition-colors" data-id="element-4037" data-mp="23982q1ql5ang">
                          <LogOut className="w-4 h-4" data-id="element-4038" data-mp="mtnbrb4mwk73" />
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Logout Button */}
            <button onClick={handleSignOut} className="p-2.5 bg-[#141414] border border-white/10 rounded-xl text-white/50 hover:text-red-400 hover:border-red-500/20 transition-colors" title="Cerrar sesión" data-id="element-4039" data-mp="cqi0ukwc6gvz">
              <LogOut className="w-5 h-5" data-id="element-4040" data-mp="3l61pf6m2bb4p" />
            </button>
          </div>
        </div>

        {/* Question */}
        <p className="text-white/50 text-lg font-medium" data-id="element-4041" data-mp="3q5o29exzapcz">
          ¿Qué deseas registrar hoy?
        </p>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4042" data-mp="3c6opp5nqq0x6">
          {ENROLLMENT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return <div key={opt.route} className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-4043" data-mp="1adh1t7u2aazo">
                <div className="space-y-4" data-id="element-4044" data-mp="2iw87dhrqnsn9">
                  <div className={`w-12 h-12 ${opt.iconBg} rounded-xl flex items-center justify-center`} data-id="element-4045" data-mp="2ivgbe5u8bswg">
                    <Icon className="w-6 h-6" data-id="element-4046" data-mp="2904xtquk4rqa" />
                  </div>
                  <div data-id="element-4047" data-mp="2dxwypad6jo6m">
                    <h2 className="text-lg font-bold text-white" data-id="element-4048" data-mp="2dz83ndid31zb">
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-4049" data-mp="31oahsbdsecp2">
                      {opt.description}
                    </p>
                  </div>
                </div>

                <button onClick={() => navigate(opt.route)} className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${opt.btnClass}`} data-id="element-4050" data-mp="2smb1jlaljcn6">
                  {(opt as any).btnLabel || 'Iniciar enrollment'}
                  <ArrowRight className="w-4 h-4" data-id="element-4051" data-mp="1lb2g6izxayvs" />
                </button>
              </div>;
        })}
        </div>

        {/* ═══ COMPACT APPLICATIONS CARD ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-4052" data-mp="18pvktnrmtra3">
          <div className="space-y-4" data-id="element-4053" data-mp="1ip356uwsam6p">
            <div className="flex items-start justify-between" data-id="element-4054" data-mp="1iq4k5agx7stp">
              <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-4055" data-mp="uz8uojzxt9pm">
                <FileText className="w-6 h-6 text-orange-400" data-id="element-4056" data-mp="q0nugkkoqq16" />
              </div>
              {stats.pending > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse" data-id="element-4057" data-mp="14pk24gity119">
                  {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                </span>}
            </div>
            <div data-id="element-4058" data-mp="7afv8dk3wjq9">
              <h2 className="text-lg font-bold text-white" data-id="element-4059" data-mp="78v0amtvqyrv">
                Aplicaciones de restaurantes
              </h2>
              <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-4060" data-mp="wxjw6wm0svhk">
                Revisa las solicitudes de ingreso a NOIZA
              </p>
            </div>

            {/* Compact Stats Row */}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-4061" data-mp="15r5cikdu2kt3">
              <div className="flex items-center gap-1.5" data-id="element-4062" data-mp="1apq27pvglm3a">
                <div className="w-2 h-2 rounded-full bg-white/30" data-id="element-4063" data-mp="1arax5glor71o" />
                <span className="text-xs text-white/50" data-id="element-4064" data-mp="e0io32x66f9e">
                  <span className="font-bold text-white" data-id="element-4065" data-mp="i0j7z331finu">{stats.total}</span>{' '}
                  total
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4066" data-mp="myaxxrk4gupe">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-4067" data-mp="rzi63rq6fvwl" />
                <span className="text-xs text-white/50" data-id="element-4068" data-mp="rwm7s2af357m">
                  <span className="font-bold text-amber-500" data-id="element-4069" data-mp="1ye9bo8q8w4wy">
                    {stats.pending}
                  </span>{' '}
                  pendientes
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4070" data-mp="3nuv4aay4i3gc">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-4071" data-mp="3ixd53c9wr2lj" />
                <span className="text-xs text-white/50" data-id="element-4072" data-mp="3iz6bqrkitmtj">
                  <span className="font-bold text-blue-500" data-id="element-4073" data-mp="3e5bjv0uri1jd">
                    {stats.contacted}
                  </span>{' '}
                  contactados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4074" data-mp="356q39b8uowl8">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-4075" data-mp="306lllhw7hvtf" />
                <span className="text-xs text-white/50" data-id="element-4076" data-mp="305ajrnmbmy4y">
                  <span className="font-bold text-emerald-500" data-id="element-4077" data-mp="2v6pirlz8wafg">
                    {stats.approved}
                  </span>{' '}
                  aprobados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4078" data-mp="tge448mv6vlp">
                <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-4079" data-mp="tdru82ci440b" />
                <span className="text-xs text-white/50" data-id="element-4080" data-mp="ohkoa9k3usid">
                  <span className="font-bold text-red-500" data-id="element-4081" data-mp="wh7gs8za1dxa">
                    {stats.rejected}
                  </span>{' '}
                  rechazados
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => setAppsModalOpen(true)} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" data-id="element-4082" data-mp="mirmjx4ydojk">
            Ver aplicaciones
            <ArrowRight className="w-4 h-4" data-id="element-4083" data-mp="ij10u7ua9ylw" />
          </button>
        </div>

        {/* ═══ ACCESO RÁPIDO KDS ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors" data-id="element-4084" data-mp="3umqzcyg99vu">
          <div className="space-y-4" data-id="element-4085" data-mp="8q22hy8zitp3">
            <div className="flex items-start justify-between" data-id="element-4086" data-mp="3qx9d25ci0crw">
              <div className="flex items-center gap-3" data-id="element-4087" data-mp="3qzvs0q1pxuk5">
                <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center" data-id="element-4088" data-mp="3uybdgpi5iuvg">
                  <ChefHat className="w-6 h-6 text-cyan-400" data-id="element-4089" data-mp="3cbvgk13vb3bk" />
                </div>
                <div data-id="element-4090" data-mp="37crb9lk2dpqi">
                  <h2 className="text-lg font-bold text-white" data-id="element-4091" data-mp="37dk7vtpb6yux">
                    Acceso rápido KDS
                  </h2>
                  <p className="text-sm text-white/40 mt-0.5" data-id="element-4092" data-mp="3l9ny7bx2an24">
                    Ir directo a la cocina de cualquier restaurante
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" data-id="element-4093" data-mp="3v7ty5nzb5j4c">
                {restaurants.length} activos
              </span>
            </div>

            {restLoading ? <div className="flex items-center justify-center py-6" data-id="element-4094" data-mp="3ur05xj7p48o">
                <Loader2 className="w-6 h-6 text-white/20 animate-spin" data-id="element-4095" data-mp="hv1p2v0yo8r7" />
              </div> : restaurants.length === 0 ? <p className="text-white/30 text-sm text-center py-4" data-id="element-4096" data-mp="cwqhys21wcco">
                No hay restaurantes activos
              </p> : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 pt-2" data-id="element-4097" data-mp="36lu8tjsas04r">
                {restaurants.map(r => {
              const isHidden = r.show_in_marketplace === false;
              const isToggling = togglingVisibilityId === r.id;
              return <div key={r.id} className="relative flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-colors group" data-id="element-4098" data-mp="3kgmt0vj4u4ho">
                      <button onClick={() => navigate(`/admin/${r.slug}/kds`)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-id="element-4099" data-mp="2arykhq5wqzf">
                        <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden shrink-0 flex items-center justify-center" data-id="element-4100" data-mp="242dd8psyho2g">
                          {r.logo_url ? <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover" data-id="element-4101" data-mp="21gx8hl63ubbk" /> : <Store className="w-5 h-5 text-white/30" data-id="element-4102" data-mp="1roht8vm700ud" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-4103" data-mp="2kf9onqvfolgn">
                          <div className="font-bold text-white text-sm truncate group-hover:text-cyan-300 transition-colors flex items-center gap-1.5" data-id="element-4104" data-mp="2keqbengqal8y">
                            {r.name}
                            {isHidden && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0" data-id="element-4105" data-mp="26g0fvc9k4tmp">
                                OCULTO
                              </span>}
                          </div>
                          <div className="text-xs text-white/30 truncate" data-id="element-4106" data-mp="2u5lb9i9hn5ks">
                            {r.slug}
                          </div>
                        </div>
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  toggleMarketplaceVisibility(r.id, r.show_in_marketplace !== false);
                }} disabled={isToggling} title={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} aria-label={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} className={`p-2 rounded-lg border transition-colors shrink-0 ${isHidden ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4107" data-mp="2l4wy0prx598y">
                        {isToggling ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-4108" data-mp="jl491crll4nr" /> : isHidden ? <EyeOff className="w-4 h-4" data-id="element-4109" data-mp="3s0nxlyhbpywh" /> : <Eye className="w-4 h-4" data-id="element-4110" data-mp="3nzc6foisx5m6" />}
                      </button>
                    </div>;
            })}
              </div>}
          </div>
        </div>

        {/* ═══ DOCUMENTOS LEGALES ═══ */}
        <div className="pt-4 border-t border-white/10" data-id="element-4111" data-mp="1h5i2au2cfols">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-4112" data-mp="1c7ypzzp7hlwh">
            Documentos legales
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-4113" data-mp="319gfdgji67pp">
            <a href="/terminos-restaurantes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4114" data-mp="3160yc869qoln">
              <FileText className="w-4 h-4 text-[#006847]" data-id="element-4115" data-mp="3jxlay0q0amdf" />
              T&C Restaurantes
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-4116" data-mp="3fycj51ej0yki" />
            </a>
            <a href="/terminos-comensales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4117" data-mp="3b61fpokkuun7">
              <FileText className="w-4 h-4 text-blue-400" data-id="element-4118" data-mp="2pghn2thhlyv" />
              T&C Comensales
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-4119" data-mp="a3h1e2ul1798" />
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══ FULL-SCREEN APPLICATIONS MANAGER MODAL ═══ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-4120" data-mp="t0rxxfotxmkz">
        {appsModalOpen && <motion.div className="fixed inset-0 z-50 flex items-stretch justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-4121" data-mp="t08suzka2sod">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAppsModalOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4122" data-mp="1bskv7maxltyi" />

            {/* Slide-over Panel */}
            <motion.div className="relative w-full sm:w-[640px] lg:w-[780px] h-full bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col overflow-hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} data-id="element-4123" data-mp="3mkvicfxo7ibo">
              {/* Panel Header */}
              <div className="shrink-0 px-6 py-5 border-b border-white/10 bg-[#0B0B0B]" data-id="element-4124" data-mp="2b3c7q2hrgd33">
                <div className="flex items-center justify-between" data-id="element-4125" data-mp="265kcozltkauo">
                  <div className="flex items-center gap-3" data-id="element-4126" data-mp="267f2ar81cr53">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-4127" data-mp="lobblvfrc7k7">
                      <FileText className="w-5 h-5 text-orange-400" data-id="element-4128" data-mp="qll2zgmpitao" />
                    </div>
                    <div data-id="element-4129" data-mp="cu6lxfcd1269">
                      <h2 className="text-xl font-bold text-white" data-id="element-4130" data-mp="csvijgmttwfo">
                        Aplicaciones de restaurantes
                      </h2>
                      <p className="text-white/40 text-xs mt-0.5" data-id="element-4131" data-mp="2vh2hi4tf9nw">
                        Centro de control de solicitudes
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAppsModalOpen(false)} className="p-2 -mr-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-4132" data-mp="3q29q13kc31mf">
                    <X className="w-6 h-6" data-id="element-4133" data-mp="1oad6p7rxcnh3" />
                  </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-3 mt-5" data-id="element-4134" data-mp="1s9431cokg135">
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-3 text-center" data-id="element-4135" data-mp="13kajgft0jo40">
                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4136" data-mp="ym92c0f5dyv6">
                      Total
                    </span>
                    <span className="text-xl font-black text-white" data-id="element-4137" data-mp="oobe3cxag22k">
                      {stats.total}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-amber-500/20 rounded-xl p-3 text-center" data-id="element-4138" data-mp="ko1a25t23lqc">
                    <span className="text-amber-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4139" data-mp="ftdj80akyf8l">
                      Pendientes
                    </span>
                    <span className="text-xl font-black text-amber-500" data-id="element-4140" data-mp="5yvcsnds45l3">
                      {stats.pending}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-3 text-center" data-id="element-4141" data-mp="3t1hy3dk074g3">
                    <span className="text-blue-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4142" data-mp="28kh57nz4diah">
                      Contactados
                    </span>
                    <span className="text-xl font-black text-blue-500" data-id="element-4143" data-mp="2hknh55f66a7j">
                      {stats.contacted}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-emerald-500/20 rounded-xl p-3 text-center" data-id="element-4144" data-mp="2mip8edmr1fu3">
                    <span className="text-emerald-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4145" data-mp="1otnb6oxa2wd8">
                      Aprobados
                    </span>
                    <span className="text-xl font-black text-emerald-500" data-id="element-4146" data-mp="1ysbgqo82tj01">
                      {stats.approved}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-red-500/20 rounded-xl p-3 text-center" data-id="element-4147" data-mp="165qlvdse6orq">
                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4148" data-mp="1b3ju0cd99sun">
                      Rechazados
                    </span>
                    <span className="text-xl font-black text-red-500" data-id="element-4149" data-mp="z20mpbnbjq6x">
                      {stats.rejected}
                    </span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex overflow-x-auto hide-scrollbar gap-2 mt-4 pb-1" data-id="element-4150" data-mp="17tu9hftfxx4y">
                  {[{
                id: 'all',
                label: 'Todos'
              }, {
                id: 'pending',
                label: 'Pendientes'
              }, {
                id: 'contacted',
                label: 'Contactados'
              }, {
                id: 'approved',
                label: 'Aprobados'
              }, {
                id: 'rejected',
                label: 'Rechazados'
              }].map(tab => <button key={tab.id} onClick={() => setStatusFilter(tab.id as any)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${statusFilter === tab.id ? 'bg-white text-[#0B0B0B]' : 'bg-[#141414] text-white/60 border border-white/10 hover:bg-white/5 hover:text-white'}`} data-id="element-4151" data-mp="3iizfnsq23xkt">
                      {tab.label}
                    </button>)}
                </div>
              </div>

              {/* Panel Content — Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-4152" data-mp="3sekclcg9q7np">
                {appsLoading ? <div className="space-y-4" data-id="element-4153" data-mp="16ohrswrprzk">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#141414] rounded-2xl border border-white/5 animate-pulse" data-id="element-4154" data-mp="owsnc5tio1t7" />)}
                  </div> : appsError ? <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center" data-id="element-4155" data-mp="2l0ynhja66shs">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" data-id="element-4156" data-mp="2g37092kgzujx" />
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-4157" data-mp="2b3kjwk9chy6w">
                      Error al cargar aplicaciones
                    </h3>
                    <p className="text-white/60 text-sm mb-4" data-id="element-4158" data-mp="38q1g1m6ce5r2">{appsError}</p>
                    <button onClick={fetchApplications} className="px-6 py-2 bg-red-500/20 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/30 transition-colors" data-id="element-4159" data-mp="33t1bxgt1zd2l">
                      Reintentar
                    </button>
                  </div> : filteredApps.length === 0 ? <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-4160" data-mp="1jbd4to9hmtum">
                    <Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" data-id="element-4161" data-mp="3uzrowt7xakad" />
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-4162" data-mp="2vfitvoobte3">
                      Aún no hay aplicaciones
                    </h3>
                    <p className="text-white/50 text-sm" data-id="element-4163" data-mp="7tqp5wfrflrk">
                      Cuando un restaurante aplique a NOIZA, aparecerá aquí.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-4164" data-mp="hjk1iznq84wv">
                    {filteredApps.map(app => <div key={app.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors relative overflow-hidden" data-id="element-4165" data-mp="mi3n2yyereop">
                        {updatingId === app.id && <div className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm z-10 flex items-center justify-center" data-id="element-4166" data-mp="2sdoi9pmhfal3">
                            <Loader2 className="w-6 h-6 text-white animate-spin" data-id="element-4167" data-mp="2xdkdp0h45tp1" />
                          </div>}

                        <div className="flex flex-col gap-4" data-id="element-4168" data-mp="3dzx8fo854wso">
                          {/* Row 1: Name + Status + Date */}
                          <div className="flex items-start justify-between gap-3" data-id="element-4169" data-mp="3j0us658stywa">
                            <div data-id="element-4170" data-mp="3nyw6ydz6zc1y">
                              <div className="flex items-center gap-2 mb-1" data-id="element-4171" data-mp="3nxuwobq02tla">
                                <h3 className="font-bold text-lg text-white" data-id="element-4172" data-mp="2qc6zvppmu998">
                                  {app.restaurant_name}
                                </h3>
                                {getStatusBadge(app.status)}
                              </div>
                              <p className="text-sm text-white/60" data-id="element-4173" data-mp="350a7t4n8jv0o">
                                {app.owner_name} • {app.business_type}
                              </p>
                            </div>
                            <p className="text-xs text-white/40 shrink-0" data-id="element-4174" data-mp="2cixyab7xes19">
                              {new Date(app.created_at).toLocaleDateString('es-CR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </p>
                          </div>

                          {/* Row 2: Quick Info */}
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-4175" data-mp="arbotzybgud4">
                            <div className="flex items-center gap-2" data-id="element-4176" data-mp="5xh9f8pqv1ju">
                              <span className="text-white/40" data-id="element-4177" data-mp="1y8e3zf22vm6">WhatsApp:</span>
                              <span className="text-white font-medium" data-id="element-4178" data-mp="3t400qotmjq48">
                                {app.whatsapp}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4179" data-mp="3j5kd4vtt2yzh">
                              <span className="text-white/40" data-id="element-4180" data-mp="3j7dga157n12q">
                                Menú digital:
                              </span>
                              <span className="text-white font-medium" data-id="element-4181" data-mp="3eh1ct3tz7du9">
                                {app.has_digital_menu ? 'Sí' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4182" data-mp="34jmwd481yp1y">
                              <span className="text-white/40" data-id="element-4183" data-mp="3s8ftv54bp4yo">Pedidos WA:</span>
                              <span className="text-white font-medium" data-id="element-4184" data-mp="11vbe9sc35d9">
                                {app.uses_whatsapp_orders ? 'Sí' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-id="element-4185" data-mp="3nf3apaiexqdb">
                            <button onClick={() => {
                      setSelectedApp(app);
                      setDetailOpen(true);
                    }} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-1.5" data-id="element-4186" data-mp="jscpp9cf5yf3">
                              <Eye className="w-4 h-4" data-id="element-4187" data-mp="298yibbkarwyh" /> Ver detalle
                            </button>
                            <button onClick={() => handleWhatsApp(app.whatsapp)} disabled={!app.whatsapp} className="px-3 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-id="element-4188" data-mp="1ljdzqts231pq">
                              <MessageCircle className="w-4 h-4" data-id="element-4189" data-mp="36wqqf0kw8iku" /> WhatsApp
                            </button>

                            <div className="flex-1" data-id="element-4190" data-mp="31yyrvnpqty7o" />

                            {app.status === 'pending' && <button onClick={() => updateStatus(app.id, 'contacted')} className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4191" data-mp="1bid72yf4bg4r">
                                Contactado
                              </button>}
                            {app.status !== 'approved' && <button onClick={() => updateStatus(app.id, 'approved')} className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4192" data-mp="245pkahze2ble">
                                Aprobar
                              </button>}
                            {app.status !== 'rejected' && <button onClick={() => updateStatus(app.id, 'rejected')} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4193" data-mp="2s7ce8smnd383">
                                Rechazar
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedApp && <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de Aplicación" data-id="element-4194" data-mp="2x6p9o1rl3ima">
          <div className="space-y-6" data-id="element-4195" data-mp="3oyn1vj1p0vx3">
            <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-4196" data-mp="3oxldkcvo2cxf">
              <div data-id="element-4197" data-mp="1n2xm8p0e5j36">
                <h3 className="text-2xl font-bold text-white" data-id="element-4198" data-mp="1s18z78lzg9pm">
                  {selectedApp.restaurant_name}
                </h3>
                <p className="text-white/60" data-id="element-4199" data-mp="1dd4czunq1g23">{selectedApp.owner_name}</p>
              </div>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-4200" data-mp="1i92xh75xidx8">
              <div data-id="element-4201" data-mp="14av4fac0jwrc">
                <p className="text-white/40 text-sm mb-1" data-id="element-4202" data-mp="14acyflzju468">WhatsApp</p>
                <p className="text-white font-medium" data-id="element-4203" data-mp="19bsjvf5n2cp5">{selectedApp.whatsapp}</p>
              </div>
              <div data-id="element-4204" data-mp="uejp0bh4mqso">
                <p className="text-white/40 text-sm mb-1" data-id="element-4205" data-mp="uf1k34s1yyz0">Instagram/Facebook</p>
                <p className="text-white font-medium" data-id="element-4206" data-mp="zdmjj1zhdeme">
                  {selectedApp.social_handle || '-'}
                </p>
              </div>
              <div data-id="element-4207" data-mp="2opor3bckev5w">
                <p className="text-white/40 text-sm mb-1" data-id="element-4208" data-mp="2tqceln1l4tvd">Tipo de negocio</p>
                <p className="text-white font-medium" data-id="element-4209" data-mp="2to8c99jc42x7">
                  {selectedApp.business_type}
                </p>
              </div>
              <div data-id="element-4210" data-mp="d4xou8a7q2p1">
                <p className="text-white/40 text-sm mb-1" data-id="element-4211" data-mp="d5h8pujfb15p">Fecha de solicitud</p>
                <p className="text-white font-medium" data-id="element-4212" data-mp="rtc2le963bkd">
                  {new Date(selectedApp.created_at).toLocaleString('es-CR')}
                </p>
              </div>
              <div data-id="element-4213" data-mp="10tgv6i9pc5eh">
                <p className="text-white/40 text-sm mb-1" data-id="element-4214" data-mp="10sz37xu2odcd">
                  ¿Usa pedidos por WhatsApp?
                </p>
                <p className="text-white font-medium" data-id="element-4215" data-mp="wrdkhvuux4u9">
                  {selectedApp.uses_whatsapp_orders ? 'Sí' : 'No'}
                </p>
              </div>
              <div data-id="element-4216" data-mp="15sbnqbxh18bs">
                <p className="text-white/40 text-sm mb-1" data-id="element-4217" data-mp="15tv402j4njdk">
                  ¿Tiene menú digital?
                </p>
                <p className="text-white font-medium" data-id="element-4218" data-mp="3cjt94r0ckay2">
                  {selectedApp.has_digital_menu ? 'Sí' : 'No'}
                </p>
              </div>
            </div>

            <div data-id="element-4219" data-mp="2lrsrwamzwtvi">
              <p className="text-white/40 text-sm mb-1" data-id="element-4220" data-mp="2lq9cem95ymus">Objetivo principal</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-4221" data-mp="2qpc5d8bvd2me">
                <p className="text-white/90" data-id="element-4222" data-mp="2vh3vjhr456c0">
                  {selectedApp.main_goal || 'No especificado'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10" data-id="element-4223" data-mp="30hzzfvbgsshf">
              <button onClick={() => handleWhatsApp(selectedApp.whatsapp)} className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-4224" data-mp="22tq1bresuovl">
                <MessageCircle className="w-5 h-5" data-id="element-4225" data-mp="2hqh2o681aanu" /> WhatsApp
              </button>

              {selectedApp.status === 'pending' && <button onClick={() => updateStatus(selectedApp.id, 'contacted')} className="flex-1 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl font-bold transition-colors" data-id="element-4226" data-mp="3o7udgg7isn39">
                  Marcar Contactado
                </button>}
              {selectedApp.status !== 'approved' && <button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-xl font-bold transition-colors" data-id="element-4227" data-mp="17plbmdd1irr2">
                  Aprobar
                </button>}
              {selectedApp.status !== 'rejected' && <button onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-bold transition-colors" data-id="element-4228" data-mp="otd1av567qow">
                  Rechazar
                </button>}
            </div>
          </div>
        </Modal>}

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-4229" data-mp="34fktlnma5i0h">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} backdrop-blur-md`} data-id="element-4230" data-mp="2zg85z0nl46wu">
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" data-id="element-4231" data-mp="2gjggg9hg8vkc" /> : <AlertCircle className="w-5 h-5" data-id="element-4232" data-mp="2glb1rowmupoy" />}
            <span className="font-bold text-sm" data-id="element-4233" data-mp="2ujhpic7vb9aw">{toast.message}</span>
          </div>
        </div>}
    </div>;
}